import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GripHorizontal } from 'lucide-react';

const CARD_WIDTH = 300;

export default function DraggableMapCard({
  selectedLead,
  mapRef,
  onClose,
  onViewDetails,
  isLoadingDetails,
}) {
  const [position, setPosition] = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const cardRef = useRef(null);

  // Initialise position from map projection whenever the selected lead changes
  useEffect(() => {
    if (!selectedLead || !mapRef?.current) return;

    try {
      const pt = mapRef.current.project([
        selectedLead.longitude,
        selectedLead.latitude,
      ]);
      setPosition({ x: pt.x - CARD_WIDTH / 2, y: pt.y - 280 });
    } catch {
      setPosition({ x: 40, y: 80 });
    }
  }, [selectedLead, mapRef]);

  // ── Mouse drag ──────────────────────────────────────────────────────────────
  const startMouseDrag = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };

    const onMove = (e) => {
      if (!isDragging.current) return;
      setPosition({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const onUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [position]);

  // ── Touch drag ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handle = cardRef.current?.querySelector('[data-drag-handle]');
    if (!handle || !position) return;

    const onTouchStart = (e) => {
      const touch = e.touches[0];
      isDragging.current = true;
      dragOffset.current = {
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      };
    };

    const onTouchMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragOffset.current.x,
        y: touch.clientY - dragOffset.current.y,
      });
    };

    const onTouchEnd = () => { isDragging.current = false; };

    handle.addEventListener('touchstart', onTouchStart, { passive: true });
    handle.addEventListener('touchmove', onTouchMove, { passive: false });
    handle.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      handle.removeEventListener('touchstart', onTouchStart);
      handle.removeEventListener('touchmove', onTouchMove);
      handle.removeEventListener('touchend', onTouchEnd);
    };
  }, [position]);

  if (!position) return null;

  const statusClass =
    selectedLead.status === 'at_risk' ? 'bg-orange-900/40 text-orange-300' :
    selectedLead.status === 'churned' ? 'bg-red-900/40 text-red-300' :
    selectedLead.category === 'current_customers' ? 'bg-emerald-900/40 text-emerald-300' :
    selectedLead.category === 'leads' ? 'bg-blue-900/40 text-blue-300' :
    selectedLead.category === 'won' ? 'bg-green-900/40 text-green-300' :
    selectedLead.category === 'planned_visits' ? 'bg-purple-900/40 text-purple-300' :
    'bg-gray-700 text-gray-300';

  return (
    <div
      ref={cardRef}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: CARD_WIDTH,
        zIndex: 60,
        userSelect: 'none',
      }}
      className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 overflow-hidden"
    >
      {/* Drag handle */}
      <div
        data-drag-handle
        onMouseDown={startMouseDrag}
        className="flex items-center justify-between px-4 pt-3 pb-1 cursor-grab active:cursor-grabbing bg-gray-900 border-b border-gray-700"
      >
        <GripHorizontal className="w-4 h-4 text-gray-400" />
        <button
          onClick={onClose}
          aria-label="Close lead card"
          className="text-gray-400 hover:text-gray-300 transition-colors text-lg leading-none"
        >
          ✕
        </button>
      </div>

      {/* Card content */}
      <div className="p-4">
        <h3 className="font-bold text-base text-gray-100 mb-3 leading-tight">
          {selectedLead.business_name || selectedLead.name || `Lead #${selectedLead.id}`}
        </h3>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 w-20 flex-shrink-0">Type:</span>
            <span className="text-gray-100 font-medium">{selectedLead.type || 'N/A'}</span>
          </div>

          {selectedLead.industry && (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20 flex-shrink-0">Industry:</span>
              <span className="text-gray-100 font-medium">{selectedLead.industry}</span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-gray-500 w-20 flex-shrink-0">Status:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusClass}`}>
              {(selectedLead.status || 'new').toUpperCase().replace('_', ' ')}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-500 w-20 flex-shrink-0">Priority:</span>
            <span className="text-gray-100 font-medium">
              {selectedLead.priority === 'high' ? '🔥🔥🔥 High' :
               selectedLead.priority === 'medium' ? '⚡⚡ Medium' : '❄️ Low'}
            </span>
          </div>

          {selectedLead.opportunity_score && (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20 flex-shrink-0">Score:</span>
              <span className="text-blue-600 font-bold">{selectedLead.opportunity_score.toFixed(1)}%</span>
            </div>
          )}

          {selectedLead.phone && (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20 flex-shrink-0">Phone:</span>
              <span className="text-gray-100 font-medium">{selectedLead.phone}</span>
            </div>
          )}

          {selectedLead.address && (
            <div className="flex items-start gap-2 pt-2 border-t border-gray-700">
              <span className="text-gray-500 w-20 flex-shrink-0">Address:</span>
              <span className="text-gray-100 text-xs">{selectedLead.address}</span>
            </div>
          )}
        </div>

        <button
          onClick={onViewDetails}
          disabled={isLoadingDetails}
          className="mt-4 w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoadingDetails ? 'Loading…' : 'View Full Details'}
        </button>
      </div>
    </div>
  );
}
